import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LockIcon from '@mui/icons-material/Lock';
import { Backdrop, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { useSnackbar } from 'notistack';
import { makeStyles } from 'tss-react/mui';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import { ConfirmDialog } from '../../shared/ConfirmDialog';
import PageLayout from '../../shared/PageLayout';
import RichTextRenderer from '../../shared/RichTextRenderer';
import { SimpleGallery } from '../../shared/SimpleGallery/SimpleGallery';
import { useBackbone } from '../providers/BackboneProvider';
import { DeleteFollowUpActionModal } from './DeleteFollowUpActionModal';
import { EditSavedFollowUpAction } from './EditSavedFollowUpAction';
import {
    FollowUpActionFileType,
    FollowUpActionImageType,
    FollowUpActionReplySchema,
    FollowUpActionType,
    ReplyFollowUpActionType,
    TaskItemContext
} from './FollowUpActions.types';
import {
    FollowUpActionsGallery,
    FollowUpActionsGalleryContainer,
    FollowUpActionsGalleryItem
} from './FollowUpActionsGallery';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: `${spacing(3)} ${spacing(2)}`,
            textTransform: 'none',
            backgroundColor: 'white'
        },
        assignContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            padding: `0 ${spacing(2)}`,
            display: 'flex',
            flexDirection: 'column'
        },
        submissionContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            padding: `${spacing(2)} ${spacing(2)}`,
            display: 'flex',
            flexDirection: 'column',
            marginBottom: spacing(8)
        },
        notAssignedCaption: {
            backgroundColor: 'white',
            padding: `${spacing(2)} ${spacing(2)}`,
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center'
        },
        lockIcon: {
            marginBottom: spacing(1)
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        addFUAButton: {
            textTransform: 'none',
            paddingLeft: spacing(2),
            alignSelf: 'flex-start'
        },
        textFieldInput: {
            marginBottom: spacing(2),
            width: '100%',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        }
    };
});

export enum FUAReplies {
    BASIC = 'basic',
    FORM = 'form',
    PHOTO = 'photo'
}

interface Position {
    coords: {
        lat: number;
        lon: number;
    };
}
interface ShowFollowUpActionProps {
    completeFormActions: (followUpAction: FollowUpActionType) => void;
    getDeviceLocation: (options: {
        success: (position: Position) => void;
        error: (error: Error) => void;
        complete: () => void;
    }) => void;
    initialFollowUpAction: FollowUpActionType;
    goToSource: (id: string | number, type: string) => void;
    onGoBack: () => void;
    onGoToLocationTaskPage: () => void;
}

export const ShowFollowUpAction = (props: ShowFollowUpActionProps) => {
    const {
        completeFormActions,
        getDeviceLocation,
        goToSource,
        initialFollowUpAction,
        onGoBack,
        onGoToLocationTaskPage
    } = props;

    const [followUpAction, setFollowUpAction] = useState(initialFollowUpAction);
    const [showSpinner, setShowSpinner] = useState(false);

    const { deleteFollowUpAction, getS3URL, onCompleteFollowUpAction, updateFollowUpAction, uploadFilesToS3 } =
        useBackbone<TaskItemContext>();

    const {
        assigned_user,
        data: { description, images = [], reply },
        date_completed,
        has_delete_permission,
        has_update_permission,
        location,
        reply_type,
        source_id,
        source_type,
        title
    } = followUpAction;

    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [galleryOpen, setGalleryOpen] = useState(false);

    const [openEditScreen, setOpenEditScreen] = useState(false);
    const [openDeleteScreen, setOpenDeleteScreen] = useState(false);

    const [openSubmissionDialog, setOpenSubmissionDialog] = useState(false);

    const [uploadedReplyImages, setUploadedReplyImages] = useState<FollowUpActionFileType[]>([]);
    const [savedReplyImages] = useState<FollowUpActionImageType[]>(reply?.images ?? []);
    const [startIndex, setStartIndex] = useState(0);

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty }
    } = useMuiForm<ReplyFollowUpActionType>({
        resolver: zodResolver(FollowUpActionReplySchema),
        defaultValues: { status: 'completed', images: [], note: '' }
    });

    const { field: imagesField } = useMuiController({ control, name: 'images' });
    const { field: noteField } = useMuiController({ control, name: 'note' });

    useEffect(() => {
        if (JSON.stringify(errors) != '{}') {
            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
        }
    }, [errors]);

    useEffect(() => {
        imagesField.onChange(uploadedReplyImages);
    }, [uploadedReplyImages]);

    const galleryItems = useMemo(
        () =>
            images?.map((s3Path, idx) => ({
                id: `${idx}-${s3Path}`,
                src: getS3URL(s3Path)
            })) ?? [],
        [images]
    );

    const toolbarMenuItems = [
        {
            title: _t('mobile_common_button_edit'),
            onClick: () => setOpenEditScreen(true),
            visible: has_update_permission
        },
        {
            title: _t('mobile_drafts_button_delete'),
            onClick: () => setOpenDeleteScreen(true),
            visible: has_delete_permission
        }
    ];

    const openGallery = (index: number) => {
        setStartIndex(index);
        setGalleryOpen(true);
    };
    const closeGallery = () => setGalleryOpen(false);

    const handleOnUploadReplyImages = (new_images: FollowUpActionFileType[]) => {
        setUploadedReplyImages((prevImages) => [...prevImages, ...new_images]);
    };

    const handleOnDeleteUploadedImage = (indexToRemove: number) => {
        setUploadedReplyImages((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const handleSuccessfulUpdate = async (updatedFollowUpAction: FollowUpActionType) => {
        updateFollowUpAction(
            updatedFollowUpAction,
            (response: FollowUpActionType) => {
                setFollowUpAction(response);
                enqueueSnackbar(_t('mobile_mobileform_follow_up_actions_update_notification'), { variant: 'success' });
            },
            () => {
                enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
            }
        );
        setOpenEditScreen(false);
    };

    const deleteFollowUpActionSuccess = async () => {
        deleteFollowUpAction(followUpAction.id, () => {
            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
        });
        setOpenDeleteScreen(false);
    };
    const onSubmit = handleSubmit(async (values) => {
        if (followUpAction.data.reply?.form?.id) {
            completeFormActions(followUpAction);
            return;
        }
        try {
            const imagesToSubmit = [
                ...uploadedReplyImages.map(
                    (uploadedFile) =>
                        ({
                            s3_key: uploadedFile.s3_key,
                            is_low_quality: uploadedFile.is_low_quality,
                            laplacian_variance: uploadedFile.laplacian_variance
                        }) as FollowUpActionImageType
                )
            ];

            if (reply_type === FUAReplies.PHOTO && !imagesToSubmit.length) {
                enqueueSnackbar(_t('mobile_mobileform_follow_up_actions_required_photo_error'), { variant: 'error' });
                return;
            }

            setShowSpinner(true);
            const updatedFollowUpAction = { ...followUpAction };
            updatedFollowUpAction.data = {
                description,
                images,
                reply: {
                    images: [''],
                    note: '',
                    status: 'completed'
                }
            };

            if (values.images) {
                updatedFollowUpAction.data.reply = {
                    images: imagesToSubmit,
                    note: values.note ?? '',
                    status: 'completed'
                };
            }

            getDeviceLocation({
                success: function (position) {
                    updatedFollowUpAction.data.reply!.coordinates = position.coords;
                },
                error: function (_error) {
                    return;
                },
                complete: function () {
                    updateFollowUpAction(
                        updatedFollowUpAction,
                        () => {
                            setShowSpinner(false);
                            onCompleteFollowUpAction('mobile_mobileform_follow_up_actions_completed_notification');
                        },
                        () => {
                            setShowSpinner(false);
                            enqueueSnackbar(_t('mobile_message_show_error_message'), { variant: 'error' });
                        }
                    );
                }
            });
        } catch {
            setShowSpinner(false);
        }
    });

    const handleGoToSource = () => {
        if (isDirty) {
            setOpenSubmissionDialog(true);
            return;
        }

        goToSource(source_id!, source_type);
    };

    const activeUserId = $f.active_user?.get('id') as number | undefined;

    const canSubmit = !date_completed && (!assigned_user?.id || assigned_user.id === activeUserId);
    const isAssignedToOther = !date_completed && assigned_user?.id && assigned_user.id !== activeUserId;

    const submitButtonText = followUpAction.data.reply?.form?.id
        ? _t('mobile_task_detials_button_goto_form')
        : _t('mobile_mobileform_follow_up_actions_complete_follow_up_action');
    return (
        <PageLayout>
            {{
                appBar: (
                    <AppBar
                        toolbarMenuItems={
                            !date_completed && (has_delete_permission || has_update_permission) && toolbarMenuItems
                        }
                        title={_t('mobile_mobileform_follow_up_actions')}
                        onGoBack={onGoBack}
                    />
                ),
                content: (
                    <>
                        <div className={classes.assignContainer}>
                            {showSpinner && (
                                <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}>
                                    <CircularProgress />
                                </Backdrop>
                            )}
                            {galleryOpen && galleryItems && (
                                <SimpleGallery items={galleryItems} onClose={closeGallery} startIndex={startIndex} />
                            )}
                            <div className="task_details">
                                <div className="card_body">
                                    <h3>{title}</h3>

                                    <div className="detail_items">
                                        {location && (
                                            <div className="detail_item task_account">
                                                <a
                                                    className="view_task_account"
                                                    href="#"
                                                    onClick={onGoToLocationTaskPage}
                                                >
                                                    <i className="df-location-filled" />
                                                    {location.name}
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {description && (
                                        <div className="task_description">
                                            <p className="task_description_text">{description}</p>
                                        </div>
                                    )}

                                    {images && images.length > 0 && (
                                        <FollowUpActionsGalleryContainer sx={(theme) => ({ mb: theme.spacing(2) })}>
                                            {images.map((s3Path, index) => (
                                                <FollowUpActionsGalleryItem
                                                    alt={`preview ${index}`}
                                                    key={index}
                                                    onClick={() => {
                                                        openGallery(index);
                                                    }}
                                                    src={getS3URL(s3Path)}
                                                />
                                            ))}
                                        </FollowUpActionsGalleryContainer>
                                    )}
                                </div>
                            </div>

                            {reply_type && reply_type == 'photo' && (
                                <>
                                    <FollowUpActionsGallery
                                        getS3URL={getS3URL}
                                        isReply
                                        onDeleteUploadedImage={handleOnDeleteUploadedImage}
                                        onUploadFiles={handleOnUploadReplyImages}
                                        photoQualityAnalysis
                                        readOnly={!!date_completed}
                                        savedFiles={savedReplyImages}
                                        uploadedFiles={uploadedReplyImages}
                                        uploadFilesToS3={uploadFilesToS3}
                                        onLoadingChange={setShowSpinner}
                                    />
                                    {!date_completed && (
                                        <TextField
                                            className={classes.textFieldInput}
                                            label={_t('mobile_field_textarea_placeholder_note')}
                                            multiline
                                            variant="outlined"
                                            {...noteField}
                                        />
                                    )}
                                </>
                            )}
                        </div>

                        <div className={classes.submissionContainer}>
                            {source_id && (
                                <div>
                                    <Typography sx={{ color: 'black', fontWeight: 'medium' }} variant="subtitle1">
                                        {_t('mobile_mobileform_follow_up_actions_navigate_to_source_title')}
                                    </Typography>
                                    <Button
                                        fullWidth
                                        onClick={handleGoToSource}
                                        sx={{ marginTop: 2, padding: 1, textTransform: 'none' }}
                                        variant="outlined"
                                    >
                                        {_t('mobile_mobileform_follow_up_actions_navigate_to_source')}
                                    </Button>
                                </div>
                            )}
                        </div>

                        <EditSavedFollowUpAction
                            followUpAction={followUpAction}
                            onGoBack={() => setOpenEditScreen(false)}
                            open={openEditScreen}
                            onSuccessfulUpdate={handleSuccessfulUpdate}
                        />

                        <DeleteFollowUpActionModal
                            description={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_subtitle')}
                            handleCloseConfirmation={() => setOpenDeleteScreen(false)}
                            no={_t('mobile_action_sheet_cancel')}
                            onSubmit={deleteFollowUpActionSuccess}
                            showConfirmation={openDeleteScreen}
                            title={_t('mobile_mobileform_follow_up_actions_forms_delete_modal_title')}
                            yes={_t('mobile_drafts_button_delete')}
                        />

                        <ConfirmDialog
                            description={_t('mobile_mobileform_follow_up_actions_source_dialog_description')}
                            cancelLabel={_t('mobile_mobileform_follow_up_actions_submission_dialog_stay')}
                            confirmLabel={_t('mobile_mobileform_follow_up_actions_submission_dialog_open_source')}
                            onCancel={() => {
                                setOpenSubmissionDialog(false);
                            }}
                            onClose={() => setOpenSubmissionDialog(false)}
                            onConfirm={() => {
                                goToSource(source_id!, source_type);
                            }}
                            open={openSubmissionDialog}
                            title={_t('mobile_mobileform_follow_up_actions_submission_dialog_title')}
                        />
                        {isAssignedToOther && (
                            <div className={classes.notAssignedCaption}>
                                <LockIcon className={classes.lockIcon} />
                                <RichTextRenderer
                                    content={_t('mobile_mobileform_follow_up_actions_error_not_assigned_to_you', {
                                        user: assigned_user?.display_name
                                    })}
                                />
                            </div>
                        )}
                    </>
                ),
                bottomBar: (
                    <BottomBar>
                        {canSubmit && (
                            <div className={classes.bottomBar}>
                                <Button
                                    className={classes.bottomButton}
                                    color="primary"
                                    disabled={showSpinner}
                                    onClick={onSubmit}
                                    variant="contained"
                                >
                                    {submitButtonText}
                                </Button>
                            </div>
                        )}
                    </BottomBar>
                )
            }}
        </PageLayout>
    );
};
